import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  Security,
  People,
  Payment,
  Warning,
  CheckCircle,
  Error,
  Info,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import { API_BASE_URL } from '../App';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({});
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [error, setError] = useState('');

  const fetchDashboardData = useCallback(async () => {
    try {
      setError('');
      const [auditRes, alertsRes, duplicatesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/payment-audit`),
        axios.get(`${API_BASE_URL}/api/payment-security-alerts?reviewed=false`),
        axios.get(`${API_BASE_URL}/api/duplicate-users`)
      ]);

      setMetrics({
        totalRevenue: auditRes.data.summary.totalRevenue,
        totalTransactions: auditRes.data.summary.totalTransactions,
        successfulTransactions: auditRes.data.summary.successfulTransactions,
        failedTransactions: auditRes.data.summary.failedTransactions,
        webhookProcessed: auditRes.data.summary.webhookProcessed,
        directApiProcessed: auditRes.data.summary.directApiProcessed,
        unreviewed: alertsRes.data.length,
        duplicateUsers: duplicatesRes.data.duplicateEmailCount,
      });

      setRecentAlerts(alertsRes.data.slice(0, 5));

      // Process payment data for charts
      const transactions = auditRes.data.transactions || [];
      const dailyData = transactions.reduce((acc, tx) => {
        if (tx.processedAt) {
          const date = new Date(tx.processedAt).toISOString().split('T')[0];
          if (!acc[date]) {
            acc[date] = { date, revenue: 0, count: 0 };
          }
          acc[date].revenue += (tx.amountKobo || 0) / 100;
          acc[date].count += 1;
        }
        return acc;
      }, {});

      setPaymentData(Object.values(dailyData).slice(-7)); // Last 7 days

    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const MetricCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card elevation={3}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getAlertIcon = (severity) => {
    switch (severity) {
      case 'HIGH': return <Error color="error" />;
      case 'MEDIUM': return <Warning color="warning" />;
      default: return <Info color="info" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard Overview
      </Typography>
      
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Chip 
          icon={<CheckCircle />} 
          label="System Operational" 
          color="success" 
          variant="outlined" 
        />
        <Typography variant="body2" color="textSecondary">
          Last updated: {new Date().toLocaleTimeString()}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Revenue"
            value={`₦${metrics.totalRevenue?.toLocaleString()}`}
            icon={<TrendingUp sx={{ fontSize: 40 }} />}
            color="success.main"
            subtitle="All time"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Transactions"
            value={metrics.totalTransactions}
            icon={<Payment sx={{ fontSize: 40 }} />}
            color="primary.main"
            subtitle={`${metrics.successfulTransactions} successful`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Security Alerts"
            value={metrics.unreviewed}
            icon={<Security sx={{ fontSize: 40 }} />}
            color={metrics.unreviewed > 0 ? "error.main" : "success.main"}
            subtitle="Unreviewed"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Duplicate Users"
            value={metrics.duplicateUsers}
            icon={<People sx={{ fontSize: 40 }} />}
            color={metrics.duplicateUsers > 0 ? "warning.main" : "success.main"}
            subtitle="Need attention"
          />
        </Grid>

        {/* Payment Processing Chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Processing (Last 7 Days)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={paymentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value, name) => [
                  name === 'revenue' ? `₦${value.toLocaleString()}` : value,
                  name === 'revenue' ? 'Revenue' : 'Transactions'
                ]} />
                <Line type="monotone" dataKey="revenue" stroke="#1976d2" strokeWidth={2} />
                <Line type="monotone" dataKey="count" stroke="#dc004e" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Processing Methods */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processing Methods
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Webhook', value: metrics.webhookProcessed },
                { name: 'Direct API', value: metrics.directApiProcessed }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Recent Security Alerts */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Security Alerts
            </Typography>
            {recentAlerts.length === 0 ? (
              <Alert severity="success">No unreviewed security alerts</Alert>
            ) : (
              <List>
                {recentAlerts.map((alert, index) => (
                  <React.Fragment key={alert.id}>
                    <ListItem>
                      <ListItemIcon>
                        {getAlertIcon(alert.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={alert.description}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              Payment Ref: {alert.paymentRef} | Amount: ₦{(alert.paymentAmount / 100).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(alert.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        label={alert.severity} 
                        color={alert.severity === 'HIGH' ? 'error' : 'warning'} 
                        size="small" 
                      />
                    </ListItem>
                    {index < recentAlerts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
